'use strict'

// HELPERS

// Cache for memoizing getItem transformations to reduce TBT
// Uses WeakMap so products can be garbage collected when no longer referenced
const itemCache = new WeakMap()

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Asynchronously generates a user object with SHA-256 hashed identifiers for GTM tracking.
 * Optimized to hash all values in parallel instead of sequentially to reduce TBT.
 *
 * @param {Object} customer - The customer object containing user information.
 * @returns {Promise<Object>} An object containing SHA-256 hashed user identifiers and membership tier.
 */
const getUser = async (customer) => {
    const isGuest = !customer.isRegistered

    // Hash all values in parallel instead of sequentially to reduce blocking time
    const [user_id_sha, customer_email_sha, customer_phone_sha, loyalty_id_sha] = await Promise.all([
        toSHA256(customer.customerId),
        !isGuest ? toSHA256(customer.email) : Promise.resolve(''),
        !isGuest ? toSHA256(customer.phoneHome) : Promise.resolve(''),
        !isGuest ? toSHA256(customer.c_loyalty_id) : Promise.resolve('')
    ])

    return {
        user_id_sha,
        customer_email_sha,
        customer_phone_sha,
        loyalty_id_sha,
        membership_tier: !isGuest ? customer.c_loyaltyMostRecentTier : ''
    }
}

/**
 * Determines and returns structured page information for Google Tag Manager (GTM) based on the current location object.
 *
 * @param {Object} location - The location object, typically from React Router or window.location, containing at least a pathname property.
 * @returns {Object} An object containing GTM page info properties such as page_name, page_category, and optionally page_section,
 *                   based on the matched route. Defaults to 'home' if no match is found or location is undefined.
 */
const getPageInfo = (location) => {
    if (!location) {
        return {
            page_name: 'home',
            page_category: 'home'
        }
    }

    switch (true) {
        case /\/confirmation/.test(location.pathname):
            return {
                page_name: 'checkout-success',
                page_category: 'checkout',
                page_section: 'success'
            }

        case /\/checkout/.test(location.pathname):
            return {
                page_name: 'checkout-review',
                page_category: 'checkout',
                page_section: 'review'
            }

        case /\/(category|([a-zA-Z]{2}\/)?[gd]\/)/.test(location.pathname):
            return {
                page_name: 'plp',
                page_category: 'browse'
            }

        case /\/search/.test(location.pathname):
            return {
                page_name: 'search',
                page_category: 'browse',
                page_section: 'search'
            }

        case /\/(product|([a-zA-Z]{2}\/)?p\/)/.test(location.pathname):
            return {
                page_name: 'pdp',
                page_category: 'product'
            }

        case /\/account/.test(location.pathname):
            return {
                page_name: 'account-home',
                page_category: 'account'
            }

        default:
            return {
                page_name: 'home',
                page_category: 'home'
            }
    }
}

/**
 * Constructs a Google Tag Manager (GTM) compatible item object from a product entity for analytics tracking.
 * Memoized to prevent redundant transformations for the same product - reduces TBT.
 *
 * @param {Object} product - The product object containing details such as id, name, price, brand, quantity, variants, and categories.
 * @param {number} index - The position of the item in the product list (used for GTM item indexing).
 * @param {Object} [recommendation] - Optional. The recommendation context, used to set item_list_id and item_list_name.
 * @param {string} basketCurrency - The currency of the basket.
 * @returns {Object} An object representing the product in GTM's expected format.
 */
const getItem = (product, index, recommendation, basketCurrency) => {
    // Check cache - WeakMap uses product object reference as key
    if (itemCache.has(product)) {
        const cached = itemCache.get(product)
        // Return cached if index and recommendation match (these affect the result)
        if (cached.index === index && cached.recommendation === recommendation) {
            return cached.result
        }
    }

    const pid = 'productId' in product ? product.productId : product.id
    const name = 'productName' in product ? product.productName : product.name
    let prodObj = {
        item_name: name?.toLowerCase(),
        item_id: pid,
        price: product.basePrice || product.price,
        currency: product.currency || product.c_currency || basketCurrency,
        quantity: product.quantity || 1,
        index: product.c_gtm_index || index || 0,
        item_group_id: pid.substring(0, pid.length - 3),
        item_list_id: recommendation ? recommendation.id : 'search_results_page',
        item_list_name: recommendation ? recommendation.name : 'search results'
    }

    if (product.brand || product.c_brand) {
        prodObj.item_brand = product.c_brand ? product.c_brand.toLowerCase() : product.brand.toLowerCase()
    }

    if (product.c_size) {
        prodObj.item_size = product.c_size.toLowerCase()
    }

    let categories

    if (product.representedProduct) {
        prodObj.sku = product.representedProduct.id

        if (product.representedProduct.c_refinementColor) {
            prodObj.item_variant = product.representedProduct.c_refinementColor.toLowerCase()
        }
        if (product.representedProduct.c_categories) {
            categories = JSON.parse(JSON.stringify(product.representedProduct.c_categories))
        }
        if (!prodObj.item_brand && product.representedProduct.c_gtmBrand) {
            prodObj.item_brand = product.representedProduct.c_gtmBrand.toLowerCase()
        }
    } else if (product.variants && product.variants.length) {
        prodObj.sku = product.variants[0].productId

        if (product.c_refinementColor) {
            prodObj.item_variant = product.c_refinementColor.toLowerCase()
        }

        if (product.c_categories) {
            categories = JSON.parse(JSON.stringify(product.c_categories))
        }
    } else {
        prodObj.sku = pid

        if (product.c_refinementColor) {
            prodObj.item_variant = product.c_refinementColor.toLowerCase()
        }

        if (product.c_categories) {
            categories = JSON.parse(JSON.stringify(product.c_categories))
        }
    }

    if (categories && categories.length) {
        categories.reverse()

        if (categories[0].length <= 1) {
            categories.shift()
        }

        if (categories.length > 0) {
            prodObj.item_category = categories[0].toLowerCase()

            if (categories.length > 1) {
                prodObj.item_category2 = categories[1].toLowerCase()

                if (categories.length > 2) {
                    prodObj.item_category3 = categories[2].toLowerCase()
                }
            }
        }
    }

    // Cache the result
    itemCache.set(product, {index, recommendation, result: prodObj})

    return prodObj
}

/**
 * Generates a summary object containing the total item count and an array of item details from a given list.
 * @param {Array<Object>} list - An array of item objects, each expected to have a 'quantity' property.
 * @param {boolean} getIndex - If true, uses a custom function to determine each item's index based on its product ID.
 * @param {Object} [recommendation] - Optional recommendation object to associate with each item; if not provided, it is determined per product.
 * @param {string} basketCurrency - The currency of the basket.
 * @returns {{count: number, items: Array<Object>}} An object with the total quantity of all items and an array of item details generated by getItem.
 */
const getItems = (list, getIndex, recommendation, basketCurrency) => {
    let itemCount = 0
    let items = []

    for (var i = 0; i < list?.length; i++) {
        let index = i
        let product = list[i]
        const pid = 'productId' in product ? product.productId : product.id

        if (getIndex) {
            index = getProductIndex(pid)

            if (index < 0) {
                index = i
            }
        }

        if (!recommendation) {
            recommendation = getRecommendation(pid)
        }

        items.push(getItem(product, index, recommendation, basketCurrency))
        itemCount += product.quantity
    }

    return {
        count: itemCount,
        items: items
    }
}

/**
 * Generates a plain JavaScript object representing the current basket/cart for GTM (Google Tag Manager) dataLayer.
 *
 * Includes details such as currency, order totals, unique item count, coupon information, shipping tier, payment types, and item details.
 * Optionally includes shipping and payment information based on provided flags.
 *
 * @param {Object} basket - The basket object containing cart data, including currency, totals, product items, shipments, payment instruments, and order price adjustments.
 * @param {boolean} includeShippingInfo - Whether to include shipping tier information from the basket's shipments.
 * @param {boolean} includePaymentInfo - Whether to include payment type and gift card usage information from the basket's payment instruments.
 * @returns {Object} cartObj - A plain object with cart summary and details, suitable for GTM dataLayer
 */
const getBasket = (basket, includeShippingInfo, includePaymentInfo) => {
    var cartObj = {
        currency: basket?.currency,
        value: basket?.orderTotal,
        cart_unique_item_count: basket?.productItems?.length,
        cart_value_net: basket?.productTotal
    }
    var isBopis = false

    if (basket.orderPriceAdjustments?.length) {
        for (var i = 0; i < basket.orderPriceAdjustments?.length; i++) {
            if (basket.orderPriceAdjustments[i].couponCode) {
                cartObj.coupon_name = basket.orderPriceAdjustments[i].couponCode
                cartObj.coupon_value = basket.orderPriceAdjustments[i].price * -1
                break
            }
        }
    }

    if (includeShippingInfo && basket.shipments?.length) {
        if (basket.shipments[0].shippingMethod) {
            const shippingMethod = basket.shipments[0].shippingMethod
            cartObj.shipping_tier = shippingMethod.name
            cartObj.shipping = shippingMethod.price
            isBopis = shippingMethod.c_storePickupEnabled
        }
    }

    if (includePaymentInfo && basket.paymentInstruments?.length) {
        let paymentType, secondaryType, secondaryAmount
        cartObj.gift_card_used = false

        for (var j = 0; j < basket.paymentInstruments.length; j++) {
            var pi = basket.paymentInstruments[j]
            if (pi.paymentMethodId == 'SMARTCLIXX') {
                if (!paymentType) {
                    paymentType = 'Gift Card'
                } else {
                    secondaryType = 'Gift Card'
                }

                cartObj.gift_card_used = true
                cartObj.gift_card_amount = pi.amount
            } else {
                if (pi.c_signifydPaymentData) {
                    try {
                        const data = JSON.parse(pi.c_signifydPaymentData)
                        let method = data.paymentMethod.type

                        if (method == 'scheme') {
                            method = 'Credit Card'
                        }

                        if (!paymentType) {
                            paymentType = method
                        } else {
                            secondaryType = method
                            secondaryAmount = pi.amount
                        }
                    } catch (error) {
                        console.error('Error parsing c_signifydPaymentData:', error)
                    }
                } else if (pi.c_Adyen_Payment_Method_Variant) {
                    let pMethod = pi.c_Adyen_Payment_Method_Variant

                    if (pMethod == 'scheme') {
                        pMethod = 'Credit Card'
                    }

                    if (!paymentType) {
                        paymentType = pMethod
                    } else {
                        secondaryType = pMethod
                        secondaryAmount = pi.amount
                    }
                } else {
                    if (!paymentType) {
                        paymentType = pi.paymentMethodId
                    } else {
                        secondaryType = pi.paymentMethodId
                        secondaryAmount = pi.amount
                    }
                }
            }
        }

        cartObj.payment_type = paymentType

        if (secondaryType) {
            cartObj.secondary_payment_type = secondaryType

            if (secondaryAmount) {
                cartObj.secondary_payment_amount = secondaryAmount
            }
        }
    }

    const list = getItems(basket.productItems, true, null, basket.currency)

    cartObj.cart_item_count = list.count
    cartObj.items = list.items

    if (includeShippingInfo || includePaymentInfo) {
        cartObj.bopis_analytics = {
            availability: isBopis,
            coverage: isBopis,
            selected: isBopis
        }
    }

    return cartObj
}

/**
 * Constructs a base object for review-related GTM (Google Tag Manager) events on the product detail screen.
 * @param {Object} product - The product object to be included in the review event.
 * @returns {Object} An object containing screen metadata and an array of items formatted for GTM.
 */
const getReviewBase = (product) => {
    return {
        screen_name: 'product detail screen',
        screen_category: 'e-commerce',
        screen_section: 'reviews_section',
        items: [getItem(product)]
    }
}

/**
 * Asynchronously computes the SHA-256 hash of a given string and returns it as a hexadecimal string.
 * @param {string} value - The input string to be hashed.
 * @returns {Promise<string>} A promise that resolves to the SHA-256 hash of the input as a hex string, or an empty string if input is falsy.
 */
const toSHA256 = async (value) => {
    if (!value) return ''

    const encoder = new TextEncoder()
    const data = encoder.encode(value)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('')
    return hashHex
}

/**
 * Saves or removes a product index entry in sessionStorage under the 'productIndices' key.
 * If isRemoval is true, removes the entry for the given productId; otherwise, adds or updates the product index.
 *
 * @param {string|number} productId - The unique identifier for the product.
 * @param {number} index - The index value to associate with the product.
 * @param {boolean} isRemoval - If true, removes the product index entry; if false, adds or updates it.
 */
const saveProductIndex = (productId, index, isRemoval) => {
    let productIndices = JSON.parse(window.sessionStorage.getItem('productIndices')) || []

    if (isRemoval) {
        productIndices = productIndices.filter((item) => item.productId !== productId)
    } else {
        productIndices.push({productId, index})
    }

    if (productIndices.length) {
        window.sessionStorage.setItem('productIndices', JSON.stringify(productIndices))
    } else {
        window.sessionStorage.removeItem('productIndices')
    }
}

/**
 * Retrieves the index of a product from sessionStorage based on the provided productId.
 * @param {string|number} productId - The unique identifier of the product to search for.
 * @returns {number} The index of the product if found; otherwise, returns 0.
 */
const getProductIndex = (productId) => {
    let productIndices = JSON.parse(window.sessionStorage.getItem('productIndices')) || []
    let index = -1

    for (var i = 0; i < productIndices.length; i++) {
        if (productIndices[i].productId === productId) {
            index = productIndices[i].index
            break
        }
    }

    return index
}

/**
 * Checks if a recommendation with the given name has already been sent and updates session storage accordingly.
 * Formats the name by converting it to lowercase and removing spaces before checking.
 *
 * @param {string} name - The name of the recommendation to check.
 * @returns {boolean} - Returns true if the recommendation has already been sent or if no name is provided; otherwise, returns false after marking it as sent.
 */
const isRecommendationSent = (name) => {
    if (!name) return true

    let recommendations = JSON.parse(window.sessionStorage.getItem('gtmRecommendations')) || []

    const nameFormatted = name.toLowerCase().replace(/\s/g, '')

    if (recommendations.indexOf(nameFormatted) < 0) {
        recommendations.push(nameFormatted)
        window.sessionStorage.setItem('gtmRecommendations', JSON.stringify(recommendations))

        return false
    }

    return true
}

/**
 * Stores a temporary recommendation name in sessionStorage under the key 'gtmTempRecommendation'.
 * The name is converted to lowercase and trimmed of whitespace before storage.
 *
 * @param {Object} obj - The recommendation object to store temporarily. If falsy, nothing is stored.
 */
const setTempRecommendation = (obj) => {
    if (obj) {
        var temp = {
            id: obj.id.toLowerCase().trim(),
            name: obj.name.toLowerCase().trim()
        }
        window.sessionStorage.setItem('gtmTempRecommendation', JSON.stringify(temp))
    }
}

/**
 * Saves or removes a product recommendation in sessionStorage under the 'gtmProductRecommendation' key.
 * If isRemoval is true, removes the product recommendation for the specified productId; otherwise, adds or updates it.
 * Cleans up the sessionStorage key if no recommendations remain.
 *
 * @param {string|number} productId - The unique identifier of the product.
 * @param {any} recommendation - The recommendation data to associate with the product.
 * @param {boolean} isRemoval - Whether to remove (true) or add/update (false) the recommendation.
 * @returns {void}
 */
const saveRecommendation = (productId, recommendation, isRemoval) => {
    let productRecommendation = JSON.parse(window.sessionStorage.getItem('gtmProductRecommendation')) || []

    if (isRemoval) {
        productRecommendation = productRecommendation.filter((item) => item.productId !== productId)
    } else {
        let found = false

        for (var i = 0; i < productRecommendation.length; i++) {
            if (productRecommendation[i].productId === productId) {
                productRecommendation[i].recommendation = recommendation
                found = true
                break
            }
        }

        if (!found) {
            productRecommendation.push({productId, recommendation})
        }
    }

    if (productRecommendation.length) {
        window.sessionStorage.setItem('gtmProductRecommendation', JSON.stringify(productRecommendation))
    } else {
        window.sessionStorage.removeItem('gtmProductRecommendation')
    }
}

/**
 * Retrieves the recommendation associated with a given product ID from sessionStorage.
 * If a temporary recommendation ('gtmTempRecommendation') exists, it is returned, removed from storage,
 * and persisted using saveRecommendation. Otherwise, searches the 'gtmProductRecommendation' array in sessionStorage
 * for a matching productId and returns its recommendation value.
 *
 * @param {string} productId - The unique identifier of the product for which to retrieve the recommendation.
 * @returns {string|undefined} The recommendation string if found; otherwise, undefined.
 */
const getRecommendation = (productId) => {
    let recommendation

    if (window.sessionStorage.getItem('gtmTempRecommendation')) {
        recommendation = JSON.parse(window.sessionStorage.getItem('gtmTempRecommendation'))
        window.sessionStorage.removeItem('gtmTempRecommendation')
        saveRecommendation(productId, recommendation, false)
        return recommendation
    }

    let productRecommendation = JSON.parse(window.sessionStorage.getItem('gtmProductRecommendation')) || []

    for (var i = 0; i < productRecommendation.length; i++) {
        if (productRecommendation[i].productId === productId) {
            recommendation = productRecommendation[i].recommendation
            break
        }
    }

    return recommendation
}

// STANDARD EVENTS

/**
 * Sends a user session event with anonymized and contextual user data to GTM.
 *
 * @param {Object} customer - The customer object containing user details.
 * @param {Object} location - The location object, typically from the router, providing page context.
 * @returns {Promise<void>} Resolves when the event has been sent.
 */
async function sendUserSession(customer, location) {
    const isGuest = !customer.isRegistered
    const parameters = {
        user_anonymous: isGuest,
        user_authenticated: !isGuest,
        user_registered: !isGuest,
        visitor_login_state: isGuest ? 'guests' : 'registered',
        brand: 'garage',
        country_code: 'gb',
        language_code: 'en',
        is_mobile: 'No'
    }

    Object.assign(parameters, getPageInfo(location))

    if (!isGuest) {
        Object.assign(parameters, await getUser(customer))
    }

    sendEvent('user_session', parameters)

    window.sessionStorage.removeItem('gtmRecommendations')
}

/**
 * Sends a login event to the tracking system with user details and login method.
 * @param {Object} customer - The customer object representing the logged-in user.
 * @param {string} method - The login method used.
 * @returns {Promise<void>} Resolves when the event has been sent; returns early if customer is not provided.
 */
async function sendLoginEvent(customer, method) {
    if (!customer) return
    let parameters = await getUser(customer)
    parameters.method = method

    sendEvent('login', parameters)
}

/**
 * Sends a Google Tag Manager 'sign_up' event with user parameters.
 * @param {string} method - The sign up method used.
 * @returns {Promise<void>} Resolves when the event has been sent.
 */
async function sendSignUpEventVerification() {
    const parameters = {
        method: 'email_password',
        sign_up_flow_type: 'native'
    }

    sendEvent('sign_up', parameters)
}

/**
 * Sends a Google Tag Manager 'sign_up' event with user parameters.
 * @param {Object} customer - The customer object containing user information.
 * @param {string} method - The sign up method used.
 * @returns {Promise<void>} Resolves when the event has been sent.
 */
async function sendSignUpEvent(customer, method) {
    if (!customer) return
    let parameters = await getUser(customer)
    parameters.method = method

    sendEvent('sign_up', parameters)
}

/**
 * Sends a Google Tag Manager 'view_item' event for a given product.
 * Constructs the event parameters including currency, price, and item details,
 * then dispatches the event using the sendEvent utility.
 *
 * @param {Object} product - The product object containing details such as currency and price.
 * @returns {void}
 */
function sendViewItemEvent(product) {
    const pid = 'productId' in product ? product.productId : product.id
    let index = getProductIndex(pid)
    let recommendation = getRecommendation(pid)

    if (index < 0) {
        index = 0
    }

    const parameters = {
        currency: product.currency || product.c_currency,
        value: product.price,
        items: [getItem(product, index, recommendation)]
    }

    sendEvent('view_item', parameters)
}

/**
 * Sends a Google Tag Manager 'view_item_list' event with the specified category and item list details.
 * @param {Object} category - The category object containing the list's id and name.
 * @param {Array} itemList - The array of items to be included in the event.
 * @param {boolean} isCarousel - Indicates if the item list is displayed in a carousel (recommendation) context.
 * @returns {void}
 */
function sendViewItemListEvent(category, itemList, isCarousel) {
    if (isCarousel && isRecommendationSent(category?.name)) {
        return
    }

    let parameters = {
        id: category?.id,
        name: category?.name?.toLowerCase()
    }

    const list = getItems(itemList, false, parameters)

    parameters.items = list.items

    sendEvent('view_item_list', parameters)
}

/**
 * Sends a Google Tag Manager event for viewing search results with the given search term and item list.
 * @param {string} searchTerm - The search term entered by the user.
 * @param {Array<Object>} itemList - The list of items resulting from the search.
 * @returns {void}
 */
function sendViewSearchResultsEvent(searchTerm, itemList) {
    let parameters = {
        search_term: searchTerm
    }

    const list = getItems(itemList, false, null)

    parameters.items = list.items

    sendEvent('view_search_results', parameters)
}

/**
 * Sends a Google Tag Manager 'select_item' event with product and category details.
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {Object} category - The category object containing the category id and name.
 * @param {number} index - The position of the item in the product list
 * @returns {void}
 */
function sendSelectItemEvent(product, category, index) {
    const parameters = {
        currency: product.currency,
        value: product.price,
        item_list_id: category?.id,
        item_list_name: category?.name,
        items: [getItem(product, index)]
    }
    const pid = 'productId' in product ? product.productId : product.id

    saveProductIndex(pid, index, false)

    sendEvent('select_item', parameters)
}

/**
 * Sends a Google Tag Manager event for adding or removing a product from the wishlist.
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {boolean} isRemoval - Indicates whether the event is for removal (true) or addition (false) to the wishlist.
 * * @param {number} index - The position of the item in the product list
 * @returns {void}
 */
function sendWishlistEvent(product, isRemoval, index) {
    const event = !isRemoval ? 'add_to_wishlist' : 'remove_from_wishlist'
    let parameters = {
        currency: product.currency,
        value: product.price,
        items: [getItem(product, index)]
    }

    if (!isRemoval) {
        parameters.interaction_context = 'product_page'
        parameters.interaction_detail = 'wishlist_button'
    }

    sendEvent(event, parameters)
}

/**
 * Sends a Google Tag Manager event for adding or removing an item from the cart.
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {boolean} isRemoval - Indicates whether the event is for item removal (true) or addition (false).
 * @returns {void}
 */
function sendItemCartEvent(product, isRemoval, interactionLocation) {
    const event = !isRemoval ? 'add_to_cart' : 'remove_from_cart'
    const pid = 'productId' in product ? product.productId : product.id
    let index = 0
    let recommendation

    if (isRemoval) {
        saveProductIndex(pid, index, true)
        saveRecommendation(pid, null, true)
    } else {
        index = getProductIndex(pid)
        recommendation = getRecommendation(pid)

        if (index < 0) {
            if (product.c_gtm_index) {
                saveProductIndex(pid, product.c_gtm_index, false)
                index = product.c_gtm_index
            } else if (product.c_variationGroup) {
                index = getProductIndex(product.c_variationGroup)

                if (index < 0) {
                    saveProductIndex(pid, 0, false)
                    index = 0
                } else {
                    saveProductIndex(product.c_variationGroup, 0, true)
                    saveProductIndex(pid, index, false)
                }
            } else {
                index = 0
            }
        }

        if (!recommendation && product.c_variationGroup) {
            recommendation = getRecommendation(product.c_variationGroup)

            if (recommendation) {
                saveRecommendation(product.c_variationGroup, null, true)
                saveRecommendation(pid, recommendation, false)
            }
        }
    }

    let parameters = {
        currency: product.c_currency || product.currency,
        value: product.price,
        ...(interactionLocation && {interaction_location: interactionLocation}),
        items: [getItem(product, index, recommendation)]
    }

    sendEvent(event, parameters)
}

/**
 * Sends a Google Tag Manager 'view_cart' event with the current basket's parameters.
 * @param {Object} basket - The current shopping basket object to extract parameters from.
 * @returns {void}
 */
function sendViewCartEvent(basket) {
    const parameters = getBasket(basket)
    sendEvent('view_cart', parameters)
}

/**
 * Sends a Google Tag Manager 'begin_checkout' event with the current basket parameters.
 * @param {Object} basket - The current shopping basket object to extract parameters from.
 * @returns {void}
 */
function sendBeginCheckoutEvent(basket) {
    const parameters = getBasket(basket)
    sendEvent('begin_checkout', parameters)
}

/**
 * Sends a Google Tag Manager event when a shipping address is added to the basket.
 * Adds the shipping tier information to the event parameters if available.
 *
 * @param {Object} basket - The current basket object containing shipment and shipping method details.
 * @returns {void}
 */
function sendShippingAddressEvent(basket) {
    const parameters = getBasket(basket, true)
    sendEvent('add_shipping_address', parameters)
}

/**
 * Sends a Google Tag Manager event for shipping information when a user adds shipping details to their basket.
 * Extracts shipping tier information from the first shipment in the basket, if available, and includes it in the event parameters.
 *
 * @param {Object} basket - The basket object containing shipment and shipping method details.
 * @returns {void}
 */
function sendShippingInfoEvent(basket) {
    const parameters = getBasket(basket, true)
    sendEvent('add_shipping_info', parameters)
}

/**
 * Sends a Google Tag Manager event for adding payment information.
 * Retrieves relevant basket parameters and triggers the 'add_payment_info' event.
 *
 * @param {Object} basket - The current basket object containing payment and order details.
 * @returns {void}
 */
function sendPaymentInfoEvent(basket) {
    const parameters = getBasket(basket, true, true)
    sendEvent('add_payment_info', parameters)
}

/**
 * Sends a Google Tag Manager 'purchase' event with order details.
 * @param {Object} order - The order object containing purchase information.
 * @returns {void}
 */
function sendPurchaseEvent(order) {
    let parameters = getBasket(order, true, true)
    parameters.affiliation = 'web'
    parameters.tax = order.taxTotal
    parameters.transaction_id = order.orderNo
    sendEvent('purchase', parameters)
    window.sessionStorage.removeItem('productIndices')
    window.sessionStorage.removeItem('gtmProductRecommendation')
}

// CUSTOM EVENTS

/**
 * Sends a 'notify_me' event to the GTM data layer with product details.
 * @param {Object} product - The product object containing details for the event.
 * @returns {void}
 */
function sendNotifyMeEvent(product) {
    const parameters = {
        currency: product.currency,
        value: product.price,
        items: [getItem(product)]
    }

    sendEvent('notify_me', parameters)
}

/**
 * Sends a 'load_review' GTM event with product review data when reviews are available.
 * @param {Object} product - The product object for which reviews are being loaded.
 * @param {Object} reviews - The reviews object containing review data, including Results and TotalResults.
 * @returns {void}
 */
function sendLoadReviewEvent(product, reviews) {
    if (!product || reviews?.Results?.length === 0) return
    let parameters = getReviewBase(product)
    parameters.review_count = reviews.TotalResults
    parameters.review_viewed = reviews.Results.length

    sendEvent('load_review', parameters)
}

/**
 * Sends a Google Tag Manager event when product reviews are sorted.
 * @param {Object} product - The product object for which the review sort event is triggered.
 * @param {string} sort - The type of sort applied to the reviews (e.g., 'newest', 'highest_rating').
 * @returns {void}
 */
function sendSortReviewEvent(product, sort) {
    if (!product) return
    let parameters = getReviewBase(product)
    parameters.sort_type = sort.toLowerCase()

    sendEvent('sort_review', parameters)
}

/**
 * Sends a Google Tag Manager event when a user initiates writing a product review.
 * Stores the current timestamp in sessionStorage under 'startReviewTime' and triggers the 'start_review' event with product-specific parameters.
 *
 * @param {Object} product - The product object for which the review is being written.
 * @returns {void}
 */
function sendStartReviewEvent(product) {
    if (!product) return
    const parameters = getReviewBase(product)

    window.sessionStorage.setItem('startReviewTime', Date.now())

    sendEvent('start_review', parameters)
}

/**
 * Sends a Google Tag Manager event when a product review is submitted, including review details and submission duration.
 * @param {Object} product - The product object for which the review is being submitted.
 * @param {Object} review - The review object containing user input.
 * @param {Object} submissionId - The unique identifier for the submitted review.
 * @returns {void}
 */
function sendSubmitReviewEvent(product, review, submissionId) {
    if (!product || !review) return
    let parameters = getReviewBase(product)

    parameters.rating = review.rating
    parameters.review_id = submissionId
    parameters.review_text = review.reviewtext
    parameters.review_length = review.reviewtext.length
    parameters.review_word_count = review.reviewtext.split(' ').length

    if (window.sessionStorage.getItem('startReviewTime')) {
        const duration = Date.now() - window.sessionStorage.getItem('startReviewTime')
        parameters.submission_duration_ms = duration
        window.sessionStorage.removeItem('startReviewTime')
    }

    sendEvent('submit_review', parameters)
}

/**
 * Sends a Google Tag Manager event indicating a failed login attempt.
 * @param {string} method - The login method used.
 * @returns {void}
 */
function sendLoginFailedEvent(method) {
    const parameters = {
        method: method
    }

    sendEvent('login_failed', parameters)
}

/**
 * Sends a 'view_error' event to the GTM data layer with the specified error type.
 * @param {string} error - The type or message of the error to be reported.
 * @returns {void}
 */
function sendViewErrorEvent(error) {
    const parameters = {
        error_type: error
    }
    sendEvent('view_error', parameters)
}

/**
 * Sends a "contact_us" event to the tracking system with customer and topic information.
 * @param {Object} customer - The customer object for whom the event is being sent.
 * @param {string} topic - The topic or subject of the contact request.
 * @returns {Promise<void>} Resolves when the event has been sent; does nothing if customer is falsy.
 */
async function sendContactUsEvent(customer, topic) {
    if (!customer) return
    let parameters = await getUser(customer)
    parameters.topic = topic

    sendEvent('contact_us', parameters)
}

/**
 * Sends a click event to the event tracking system with the specified type.
 * @param {string} type - The type of click event to be sent.
 * @returns {void}
 */
function sendClickEvent(type) {
    const parameters = {
        type: type
    }
    sendEvent('custom_click', parameters)
}

/**
 * Sends a custom event to the Google Tag Manager dataLayer with specified parameters.
 * @param {string} eventName - The name of the event to be pushed to the dataLayer.
 * @param {Object} parameters - Additional key-value pairs to include with the event.
 * @returns {void}
 */
const sendEvent = (eventName, parametersObj) => {
    if (typeof window === 'undefined') return

    const config = getConfig();
    var parameters = parametersObj;
    var gtmAddGA4Wrapper = config.app.GTM_ADD_GA4WRAPPER;

    if (gtmAddGA4Wrapper === 'true' || gtmAddGA4Wrapper === true) {
        if (eventName != 'user_session') {
            if (eventName != 'login' && eventName != 'sign_up' && eventName != 'login_failed') {
                parameters = {
                    ga4eec: {
                        ecommerce: parametersObj
                    }
                }
            }
        } else {
            parameters = {
                ecommerce: parametersObj,
                page: {
                    country_code: 'gb',
                    brand: 'garage'
                }
            }
        }
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ ga4eec: { ecommerce: null } })
    window.dataLayer.push({
        event: eventName,
        ...parameters
    })
}

export {
    sendUserSession,
    sendLoginEvent,
    sendSignUpEvent,
    sendViewItemEvent,
    sendViewItemListEvent,
    sendViewSearchResultsEvent,
    sendSelectItemEvent,
    sendWishlistEvent,
    sendItemCartEvent,
    sendViewCartEvent,
    sendBeginCheckoutEvent,
    sendShippingAddressEvent,
    sendShippingInfoEvent,
    sendPaymentInfoEvent,
    sendPurchaseEvent,
    sendNotifyMeEvent,
    sendLoadReviewEvent,
    sendSortReviewEvent,
    sendStartReviewEvent,
    sendSubmitReviewEvent,
    sendLoginFailedEvent,
    sendViewErrorEvent,
    sendContactUsEvent,
    sendClickEvent,
    sendEvent,
    setTempRecommendation,
    sendSignUpEventVerification,
    toSHA256
}
